import React, { useEffect, useMemo, useState } from 'react';
import API from '../../../Utils/api';
import Table from '../../../Components/Table/Table';
import { useUser } from '../../../Context/useUser';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { Link } from 'react-router';
import { FaUserAlt, FaSearch } from 'react-icons/fa';
import Avatar from 'react-avatar';
import styles from './DashboardPage.module.css';

export default function AthleteList() {
    const { user } = useUser();
    const { coachId } = useParams();
    const visitingCoach = useVisitingUser(coachId);
    // Only use visitingCoach if coachId exists AND user is not a coach (i.e., admin viewing coach's dashboard)
    const currentCoach = (coachId && user?.type !== 'Coach') ? visitingCoach : user;
    const [relationships, setRelationships] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!currentCoach) return;
        API.getAthletesWithLastWorkoutById(currentCoach._id).then(({ data }) => {
            setRelationships(data);
        });
    }, [currentCoach]);

    const athletes = useMemo(() => {
        if (!relationships) return [];
        return relationships.filter(
            r =>
                r.athlete &&
                (r.athlete.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                    r.athlete.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                    r.athlete.email?.toLowerCase().includes(search.toLowerCase()))
        );
    }, [relationships, search]);

    const columns = [
        {
            Header: 'Athlete Name',
            accessor: r => `${r.athlete.firstName} ${r.athlete.lastName}`,
            Cell: ({ row }) => (
                <div className={styles.athleteCell}>
                    <Avatar
                        name={`${row.original.athlete.firstName} ${row.original.athlete.lastName}`}
                        size='40'
                        round={true}
                        className={styles.athleteAvatar}
                    />
                    <div className={styles.athleteInfo}>
                        <Link
                            to={`/athlete/${row.original.athlete._id}/profile`}
                            className={styles.athleteName}
                        >
                            {row.original.athlete.firstName} {row.original.athlete.lastName}
                        </Link>
                        <span className={styles.athleteEmail}>
                            {row.original.athlete.email}
                        </span>
                    </div>
                </div>
            )
        },
        {
            id: 'currentCSS',
            Header: () => <div style={{ textAlign: 'center' }}>Current CSS</div>,
            accessor: r => r.athlete.currentCSS,
            Cell: ({ row }) =>
                row.original.athlete.currentCSS ? (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.metricValue}>
                            {Number(row.original.athlete.currentCSS).toFixed(2)}
                        </span>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.noData}>-</span>
                    </div>
                )
        },
        {
            id: 'lastWorkoutVolume',
            Header: () => <div style={{ textAlign: 'center' }}>Last Workout Volume (m)</div>,
            accessor: r => r.lastWorkout?.volume,
            Cell: ({ row }) =>
                row.original.lastWorkout?.volume ? (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.metricValue}>
                            {row.original.lastWorkout.volume}m
                        </span>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.noData}>-</span>
                    </div>
                )
        },
        {
            id: 'lastWorkoutDate',
            Header: () => <div style={{ textAlign: 'center' }}>Last Workout Date</div>,
            accessor: r => r.lastWorkout?.workoutStart,
            Cell: ({ row }) =>
                row.original.lastWorkout ? (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.dateValue}>
                            {new Date(row.original.lastWorkout.workoutStart).toLocaleDateString('el-GR')}
                        </span>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.noData}>-</span>
                    </div>
                )
        },
        {
            id: 'trainingStress',
            Header: () => <div style={{ textAlign: 'center' }}>Training Stress</div>,
            accessor: r => r.lastWorkout?.tss,
            Cell: ({ row }) =>
                row.original.lastWorkout?.tss ? (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.stressValue}>
                            {row.original.lastWorkout.tss.toFixed(0)}
                        </span>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.noData}>-</span>
                    </div>
                )
        }
    ];

    return (
        <div className={styles.athleteSection}>
            <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>
                    <FaUserAlt className={styles.sectionIcon} />
                    <h3>Athletes</h3>
                </div>
                <span className={styles.badge}>{athletes.length}</span>
            </div>

            <div className={styles.searchContainer + ' island'}>
                <FaSearch className={styles.searchIcon} />
                <input
                    type='search'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder='Search athletes by name or email...'
                    className={styles.searchInput}
                />
            </div>

            <div className={styles.tableContainer + ' island'}>
                <Table columns={columns} data={athletes} rowsPerPage={10} />
            </div>
        </div>
    );
}
